import React from "react";
//#region redux
import { useParams } from "react-router-dom";
//#endregion
//#region types
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { CurrencyEnums, FormEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { EntityIdParams } from "models/propTypes/urlParamsTypes";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import {
  GenericForm,
  PageLayouts,
  EntityTitle,
  RouteBreadCrumb,
  LoadingWithLogo,
  ViewPanel,
  FileViewerContainer,
  useMountedState,
  useNavigationHistory,
} from "components/common";
import { ErrorView } from "components/error";
import { Vouchers } from "components/voucher";
import useCashReconciliationPermissions from "components/cashReconciliation/hooks/useCashReconciliationPermissions";
import { useRevenueReportingService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const itemGroups = [
  {
    label: TranslationKeys.Common.Sale,
    icon: IconDefinitionConstants.WMoney,
    category: ApiModels.RevenueReportItemCategory.Sales,
    totalKey: "saleTotal",
  },
  {
    label: TranslationKeys.Common.PaymentMethod,
    icon: IconDefinitionConstants.WCreditCard,
    category: ApiModels.RevenueReportItemCategory.PaymentMethod,
    totalKey: "paymentMethodsTotal",
  },
  {
    label: TranslationKeys.RevenueReporting.Other,
    icon: IconDefinitionConstants.WTransactions,
    category: ApiModels.RevenueReportItemCategory.Other,
    totalKey: "otherTotal",
  },
];

const RevenueReportView: React.FC = () => {
  const revenueReportingService = useRevenueReportingService();
  const { canViewCashReconciliations, revenueReportingTemplatesLoading } = useCashReconciliationPermissions();
  const { getMounted } = useMountedState();
  const { goBack } = useNavigationHistory();
  const urlParams = useParams<EntityIdParams>();
  const formRef = React.useRef<FormHandles>(null);

  const [loading, setLoading] = React.useState(true);
  const [report, setReport] = React.useState<ApiModels.RevenueReportDetail>();
  const goBackTimeout = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    const id = urlParams.id;
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    revenueReportingService
      .getReport(id)
      .then((data) => {
        if (getMounted()) {
          setReport(data);
        }
      })
      .catch(() => {
        if (getMounted()) {
          goBackTimeout.current = setTimeout(() => goBack(), 1000);
        }
      })
      .finally(() => {
        if (getMounted()) {
          setLoading(false);
        }
      });

    return () => {
      if (goBackTimeout.current) {
        clearTimeout(goBackTimeout.current);
      }
    };
  }, [urlParams]);

  const formatCurrency = (amount: number) =>
    LocalizationHelperInstance.getFormattedCurrency(amount, CurrencyEnums.CurrencyCode.SEK);

  const itemsForCategory = (category: ApiModels.RevenueReportItemCategory) =>
    (report?.items ?? []).filter((item) => item.category === category);

  const buildFields = () =>
    itemGroups
      .map((group) => ({ group, items: itemsForCategory(group.category) }))
      .filter(({ items }) => items.length > 0)
      .map(({ group, items }) => ({
        label: group.label,
        type: FormEnums.FormSectionType.FIELDGROUPSECTION,
        icon: group.icon,
        innerFields: {
          ...items.reduce((fields: any, item, index) => {
            fields[`item_${group.category}_${index}`] = {
              label: item.description ?? "",
              type: FormEnums.FormFieldType.TEXT,
            };
            return fields;
          }, {}),
          [group.totalKey]: {
            label: TranslationKeys.Common.InTotal,
            type: FormEnums.FormFieldType.TEXT,
          },
        },
      }));

  const getDefaultValues = () => {
    const values: any = {};
    itemGroups.forEach((group) => {
      const items = itemsForCategory(group.category);
      let total = 0;
      items.forEach((item, index) => {
        values[`item_${group.category}_${index}`] = formatCurrency(item.amount);
        total += item.amount;
      });
      values[group.totalKey] = formatCurrency(total);
    });
    return values;
  };

  const getAttachmentsMeta = (): ImageMeta[] | undefined =>
    report?.attachments && report.attachments.length > 0
      ? report.attachments.map((a) => ({
          id: a.id ?? "",
          fileName: a.fileName,
          contentType: a.contentType,
          numberOfPages: 1,
        }))
      : undefined;

  const getAttachmentData = async (fileId: string | number) => {
    if (!report?.id) {
      return "";
    }
    const attachment = await revenueReportingService.getAttachment(report.id, fileId as string);
    return attachment.data ?? "";
  };

  const fileViewerComponent = React.useMemo(
    () => (
      <FileViewerContainer
        meta={getAttachmentsMeta()}
        parentId={0}
        loading={loading}
        getFile={getAttachmentData}
      />
    ),
    [report, loading]
  );

  const upperLeftComponent = report ? (
    <EntityTitle
      status={LocalizationHelperInstance.getFormattedMessage(
        report.status === ApiModels.RevenueReportStatus.Voided
          ? TranslationKeys.RevenueReporting.StatusVoided
          : TranslationKeys.RevenueReporting.StatusReported
      )}
      subTitle={report.templateName ?? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.Reports)}
      title={report.date ? LocalizationHelperInstance.getFormattedDate(report.date) : ""}
    />
  ) : (
    <></>
  );

  return (
    <PageLayouts.EntityFullScreen
      entityKey="revenue-report"
      id={0}
      navigation={
        <RouteBreadCrumb
          current={loading ? "" : report?.date ? LocalizationHelperInstance.getFormattedDate(report.date) : ""}
          showHome
        />
      }
      hasPermissions={revenueReportingTemplatesLoading ? undefined : canViewCashReconciliations()}
    >
      {loading || revenueReportingTemplatesLoading ? (
        <LoadingWithLogo />
      ) : report ? (
        <PageLayouts.EntityViewLayout
          displayType={StyleEnums.DisplayType.Info}
          upperLeftComponent={upperLeftComponent}
          upperRightComponent={<></>}
          lowerLeftComponent={fileViewerComponent}
        >
          <>
            <ViewPanel
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
              icon={IconDefinitionConstants.WEdit}
              classes={{ content: cssExports.detailsFormContainer }}
            >
              <GenericForm
                formRef={formRef}
                fields={buildFields()}
                defaultValues={getDefaultValues()}
                isReadOnly
                inline
                hideSuccess
              />
            </ViewPanel>
            <Vouchers parentId={0} getVouchersHandler={() => revenueReportingService.getTransactions(report.id ?? "")} />
          </>
        </PageLayouts.EntityViewLayout>
      ) : (
        <ErrorView />
      )}
    </PageLayouts.EntityFullScreen>
  );
};

export default RevenueReportView;
